import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import RolePermissionContainer from "./rolerpermissioncontainer";
import RolePermissionForm from "./rolepermissionform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchRolePermissionsThunk,
  addRolePermissionThunk,
  deleteRolePermissionThunk,
  fetchRolesThunk,
  fetchPermissionsThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionEnum } from "../../../Components/constants/permissions";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  rolePermissions: rbac.rolePermissions,
  roles: rbac.roles,
  permissions: rbac.permissions,
  loading: rbac.loading,
}));

const RolePermissionPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { rolePermissions, roles, permissions, loading } = useSelector(rbacSelector);
  const userPermissions = usePermissions();

  const rolePermissionList = rolePermissions || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRolePermissionId, setSelectedRolePermissionId] = useState<number | null>(null);

  const loadRolePermissionList = useCallback(() => {
    return dispatch(fetchRolePermissionsThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchRolePermissionsThunk());
    dispatch(fetchRolesThunk());
    dispatch(fetchPermissionsThunk());
  }, [dispatch]);

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedRolePermissionId(id);
    setDeleteModalOpen(true);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        roleid: Yup.number().required("Role is required"),
        permissionid: Yup.number()
          .required("Permission is required")
          .test(
            "duplicate-check",
            "This permission is already assigned to the selected role",
            function (value) {
              const roleid = this.parent.roleid;
              if (!roleid || !value) return true;
              const selectedRole = roles?.find((r: any) => r.roleid === Number(roleid));
              if (!selectedRole) return true;            
              const isDuplicate = rolePermissionList.some((rp: any) => {
                const roleMatches = rp.roleid !== undefined 
                  ? rp.roleid === Number(roleid)
                  : rp.rolename?.toLowerCase() === selectedRole.rolename?.toLowerCase();                
                const permissionMatches = rp.permissionid !== undefined
                  ? rp.permissionid === Number(value)
                  : (() => {
                      const selectedPermission = permissions?.find((p: any) => p.permissionid === Number(value));
                      return selectedPermission && rp.permissionname?.toLowerCase() === selectedPermission.permissionname?.toLowerCase();
                    })();
                
                return roleMatches && permissionMatches;
              });              
              return !isDuplicate;
            }
          ),
      }),
    [rolePermissionList, roles, permissions]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      roleid: "",
      permissionid: "",
    },
    validationSchema,
    onSubmit: async (values, { setFieldValue, setFieldTouched }) => {
      try {
        const res = await dispatch(
          addRolePermissionThunk({
            roleid: Number(values.roleid) || 0,
            permissionid: Number(values.permissionid) || 0,
          })
        );
        if (!res?.success) {
          toast.error(res?.message || "Failed to add role permission");
          return;
        }
        toast.success("Role permission added successfully!");
        await dispatch(fetchRolePermissionsThunk());
        setFieldValue("permissionid", "");
        setFieldTouched("permissionid", false);
      } catch (err) {
        console.error("Error saving role permission:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm();
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const filteredRolePermissions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rolePermissionList;
    return rolePermissionList.filter((t: any) =>
      t.permissionname?.toLowerCase().includes(q) ||
      t.rolename?.toLowerCase().includes(q)
    );
  }, [rolePermissionList, searchQuery]);

  const columns = useMemo(
    () => [
      {
        header: "Role Permission ID",
        accessorKey: "rolepermissionid",
        cell: ({ row }: any) => {
          const id = row.original.rolepermissionid;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Role Name", accessorKey: "rolename" },
      { header: "Permission Name", accessorKey: "permissionname" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      ...(userPermissions.includes(PermissionEnum.UPDATE_ROLEPERMISSION) || userPermissions.includes(PermissionEnum.DELETE_ROLEPERMISSION) ? [{
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
  
          return (
            <div className="d-flex justify-content-center align-items-center">
              {userPermissions.includes(PermissionEnum.DELETE_ROLEPERMISSION) && (
                <Button
                  color=""
                  className="btn btn-icon btn-soft-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(row.rolepermissionid);  
                  }}
                  title="Delete"
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
              )}
            </div>
          );
        },
      }] : []),
    ],
    [handleDeleteClick, userPermissions]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Role Permission" pageTitle={["RBAC", "Role Permissions"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={3}>
            {/* <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Permission..."
                onChange={(e) => setSearchQuery(e.target.value)}
              /> */}
              {/* <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div> */}
          </Col>
          <Col md={9} className="text-end">
          {userPermissions.includes(PermissionEnum.ADD_ROLEPERMISSION) && (
            <Button color="primary" onClick={toggleModal}>
              + Add New
            </Button>
          )}
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredRolePermissions.length > 0 ? (
                    <RolePermissionContainer
                      columns={columns}
                      data={filteredRolePermissions}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          Add New Role Permission
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <RolePermissionForm 
              validation={validation} 
              roles={roles}
              permissions={permissions}
              rolePermissions={rolePermissionList}
            />
          </ModalBody>
          <ModalFooter>
            {userPermissions.includes(PermissionEnum.ADD_ROLEPERMISSION) && (
              <Button
                type="submit"
                color="primary"
              >
                Add
              </Button>
            )}
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Role Permission"
        message="Are you sure you want to delete this Role Permission?"
        onConfirm={async () => {
          if (selectedRolePermissionId) {
            setDeleteModalOpen(false);
            const rolepermissionid = selectedRolePermissionId;
            setSelectedRolePermissionId(null);
            try {
              const res = await dispatch(
                deleteRolePermissionThunk(rolepermissionid)
              );
              if (!res?.success) {
                toast.error(res?.message || "Failed to delete role permission");
                return;
              }
              toast.success("Role permission deleted successfully!");
              await dispatch(fetchRolePermissionsThunk());
            } catch (err) {
              console.error("Error deleting role permission:", err);
              toast.error("An error occurred. Please try again.");
            }
          }
        }}
      />
    </div>
  );
};

export default RolePermissionPage;

